import { Calendar, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { OpsOption, QueryType } from "../types";

interface QueryFormProps {
  fundName: string;
  opsName: string;
  queryType: QueryType;
  phoneNumber: string;
  startDate: Date;
  endDate: Date;
  opsOptions: OpsOption[];
  isLoading: boolean;
  onOpsChange: (value: string) => void;
  onQueryTypeChange: (type: QueryType) => void;
  onPhoneNumberChange: (value: string) => void;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  onLast30Minutes: () => void;
  onSubmit: () => void;
}

export function QueryForm({
  fundName,
  opsName,
  queryType,
  phoneNumber,
  startDate,
  endDate,
  opsOptions,
  isLoading,
  onOpsChange,
  onQueryTypeChange,
  onPhoneNumberChange,
  onStartDateChange,
  onEndDateChange,
  onLast30Minutes,
  onSubmit,
}: QueryFormProps) {
  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleDateChange = (
    value: string,
    setter: (date: Date) => void
  ) => {
    const date = new Date(value);
    setter(date);
  };

  return (
    <div className="flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardContent className="p-6 space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground">
              Escolha o Chat
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Consulte mensagens do WhatsApp
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ops-select">Operação</Label>
            <Select value={opsName} onValueChange={onOpsChange}>
              <SelectTrigger id="ops-select">
                <SelectValue placeholder="Selecione uma operação" />
              </SelectTrigger>
              <SelectContent>
                {opsOptions?.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {option.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Button
              variant="default"
              onClick={() => onQueryTypeChange("all")}
              className="w-full"
              disabled={!opsName}
            >
              Consultar Todos
            </Button>

            <Button
              variant="outline"
              onClick={() => onQueryTypeChange("single")}
              className="w-full"
              disabled={!opsName}
            >
              Consulta Individual
            </Button>
          </div>

          {queryType && (
            <>
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-4">
                  Informações da Consulta
                </h3>

                {queryType === "single" && (
                  <div className="space-y-2 mb-4">
                    <Label htmlFor="phone-input">Número de Telefone</Label>
                    <Input
                      id="phone-input"
                      type="text"
                      placeholder="+55 (11) 98765-4321"
                      value={phoneNumber}
                      onChange={(e) => onPhoneNumberChange(e.target.value)}
                      maxLength={19}
                    />
                  </div>
                )}

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Data/Hora Início</Label>
                    <div className="relative">
                      <Input
                        id="start-date"
                        type="datetime-local"
                        value={formatDateForInput(startDate)}
                        onChange={(e) =>
                          handleDateChange(e.target.value, onStartDateChange)
                        }
                      />
                      <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-muted-foreground pointer-events-none" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end-date">Data/Hora Fim</Label>
                    <div className="relative">
                      <Input
                        id="end-date"
                        type="datetime-local"
                        value={formatDateForInput(endDate)}
                        onChange={(e) =>
                          handleDateChange(e.target.value, onEndDateChange)
                        }
                      />
                      <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-muted-foreground pointer-events-none" />
                    </div>
                  </div>

                  <Button
                    variant="secondary"
                    onClick={onLast30Minutes}
                    className="w-full"
                  >
                    Últimos 30 minutos
                  </Button>

                  <Button
                    onClick={onSubmit}
                    className="w-full"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Buscando...
                      </>
                    ) : (
                      "Buscar"
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
